import { useCallback, useEffect, useRef, useState } from 'react';

type OverflowResult = {
  containerRef: React.RefObject<HTMLDivElement>;
  visibleCount: number;
};

/**
 * Measures how many POI tags fit inside the container and returns the visible count.
 * The remaining POIs (total - visibleCount) will be collapsed into a "+N" tag.
 * Uses ResizeObserver to recalculate on container resize.
 *
 * Tag widths are cached the first time all tags are in the DOM so that subsequent
 * resize calculations can correctly increase visibleCount even when fewer tags
 * are currently rendered.
 *
 * @param totalCount - total number of POI tags
 * @param overflowTagWidth - reserved px for the "+N" tag (default 60)
 */
export function usePoiTagOverflow(totalCount: number, overflowTagWidth = 60): OverflowResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(totalCount);
  const tagWidthsRef = useRef<number[]>([]);

  // Reset when the list changes (e.g. new POIs loaded)
  useEffect(() => {
    setVisibleCount(totalCount);
    tagWidthsRef.current = [];
  }, [totalCount]);

  const recalculate = useCallback(() => {
    const container = containerRef.current;

    if (!container || totalCount === 0) {
      setVisibleCount(totalCount);
      return;
    }

    const containerWidth = container.clientWidth;
    if (containerWidth === 0) {
      return;
    }

    const domTags = Array.from(container.children).filter(
      el => !el.classList.contains('poiTagBar__tag--overflow')
    ) as HTMLElement[];

    // Cache widths the first time all tags are in the DOM
    if (domTags.length === totalCount) {
      tagWidthsRef.current = domTags.map(el => el.offsetWidth);
    }

    const widths = tagWidthsRef.current;
    if (widths.length === 0) {
      return;
    }

    const gap = 8; // matches SCSS gap between tags

    let usedWidth = 0;
    let count = 0;

    for (let i = 0; i < totalCount; i++) {
      const tagWidth = (widths[i] ?? widths[widths.length - 1] ?? 0) + (i > 0 ? gap : 0);
      const wouldNeedOverflow = i < totalCount - 1;
      const spaceForOverflow = wouldNeedOverflow ? overflowTagWidth + gap : 0;

      if (usedWidth + tagWidth + spaceForOverflow > containerWidth) {
        break;
      }

      usedWidth += tagWidth;
      count++;
    }

    setVisibleCount(count === 0 ? 1 : count);
  }, [totalCount, overflowTagWidth]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const observer = new ResizeObserver(recalculate);
    observer.observe(container);

    // Double RAF ensures the browser has completed layout before measuring,
    // avoiding a stale clientWidth of 0 on the first paint.
    let rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(recalculate);
    });

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [recalculate]);

  return { containerRef, visibleCount };
}
